import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Edit, Shield, User, Settings as SettingsIcon } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const AdminUsersPage = () => {
  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState(localStorage.getItem('role') || 'service_coordinator');
  
  const roleColors = {
    managing_director: 'bg-red-500/20 text-red-400 border-red-500/30',
    administration_manager: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    service_coordinator: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    account_manager: 'bg-green-500/20 text-green-400 border-green-500/30'
  };
  
  const roleLabels = {
    managing_director: 'Managing Director',
    administration_manager: 'Administration Manager',
    service_coordinator: 'Service Coordinator',
    account_manager: 'Account Manager'
  };
  
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(null);
  
  const handleRoleChange = async (adminId, newRole) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(
        `${API}/admins/${adminId}`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Role updated to ${roleLabels[newRole]}`);
      setRoleDropdownOpen(null);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  
  const [createFormData, setCreateFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'service_coordinator'
  });

  const [editFormData, setEditFormData] = useState({
    email: '',
    role: '',
    password: ''
  });

  useEffect(() => {
    if (['managing_director', 'administration_manager'].includes(currentRole)) {
      fetchAdmins();
    } else {
      setIsLoading(false);
    }
  }, [currentRole]);

  const fetchAdmins = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied: Super admin privileges required');
      } else {
        toast.error('Failed to load admins');
      }
      console.error('Error fetching admins:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      await axios.post(
        `${API}/admins/create`,
        createFormData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Admin created successfully!');
      setIsCreateDialogOpen(false);
      setCreateFormData({ username: '', email: '', password: '', role: 'admin' });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
      console.error('Error creating admin:', error);
    }
  };

  const handleEditAdmin = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      const updateData = {};
      if (editFormData.email) updateData.email = editFormData.email;
      if (editFormData.role) updateData.role = editFormData.role;
      if (editFormData.password) updateData.password = editFormData.password;

      await axios.put(
        `${API}/admins/${selectedAdmin.id}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Admin updated successfully!');
      setIsEditDialogOpen(false);
      setSelectedAdmin(null);
      setEditFormData({ email: '', role: '', password: '' });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admin');
      console.error('Error updating admin:', error);
    }
  };

  const deleteAdmin = async (adminId) => {
    const token = localStorage.getItem('token');
    if (!confirm('Are you sure you want to delete this admin?')) return;

    try {
      await axios.delete(`${API}/admins/${adminId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Admin deleted successfully!');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin');
      console.error('Error deleting admin:', error);
    }
  };

  const openEditDialog = (admin) => {
    setSelectedAdmin(admin);
    setEditFormData({
      email: admin.email,
      role: admin.role,
      password: ''
    });
    setIsEditDialogOpen(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!['managing_director', 'administration_manager'].includes(currentRole)) {
    return (
      <AdminLayout>
        <div data-testid="admin-users-page" className="flex items-center justify-center h-96">
          <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-8">
            <CardContent className="text-center">
              <Shield className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Restricted Access</h2>
              <p className="text-gray-400">You need Managing Director or Administration Manager privileges to manage admin users.</p>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div data-testid="admin-users-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Admin Users</h1>
            <p className="text-gray-400">Manage admin accounts, permissions, and register new users</p>
          </div>
          
          <div className="flex gap-3">
            {currentRole === 'managing_director' && (
              <Button 
                onClick={() => window.location.href = '/admin/permissions'}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <SettingsIcon className="w-5 h-5 mr-2" />
                Manage Permissions
              </Button>
            )}
            
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-admin-btn" className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white border-0">
                  <Plus className="w-5 h-5 mr-2" />
                  Create Admin
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 border-white/10">
              <DialogHeader>
                <DialogTitle className="text-2xl text-white">Create New Admin</DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleCreateAdmin} className="space-y-4">
                <div>
                  <Label htmlFor="create_username" className="text-gray-300">Username</Label>
                  <Input
                    id="create_username"
                    data-testid="create-username-input"
                    value={createFormData.username}
                    onChange={(e) => setCreateFormData({ ...createFormData, username: e.target.value })}
                    required
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                
                <div>
                  <Label htmlFor="create_email" className="text-gray-300">Email</Label>
                  <Input
                    id="create_email"
                    data-testid="create-email-input"
                    type="email"
                    value={createFormData.email}
                    onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                    required
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                
                <div>
                  <Label htmlFor="create_password" className="text-gray-300">Password</Label>
                  <Input
                    id="create_password"
                    data-testid="create-password-input"
                    type="password"
                    value={createFormData.password}
                    onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
                    required
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                
                <div>
                  <Label htmlFor="create_role" className="text-gray-300">Role</Label>
                  <Select value={createFormData.role} onValueChange={(value) => setCreateFormData({ ...createFormData, role: value })}>
                    <SelectTrigger data-testid="create-role-select" className="bg-white/10 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-white/20">
                      <SelectItem value="managing_director" className="text-white hover:bg-white/10">Managing Director</SelectItem>
                      <SelectItem value="administration_manager" className="text-white hover:bg-white/10">Administration Manager</SelectItem>
                      <SelectItem value="service_coordinator" className="text-white hover:bg-white/10">Service Coordinator</SelectItem>
                      <SelectItem value="account_manager" className="text-white hover:bg-white/10">Account Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3">
                  <Button type="submit" data-testid="submit-create-admin-btn" className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white border-0">
                    Create Admin
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)} className="border-white/20 text-white hover:bg-white/10">
                    Cancel
                  </Button>
                </div>
              </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-400">Loading admins...</p>
          </div>
        ) : admins.length === 0 ? (
          <Card className="bg-white/5 backdrop-blur-sm border-white/10">
            <CardContent className="py-12 text-center">
              <User className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No admin users found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {admins.map((admin, index) => (
              <Card key={admin.id} data-testid={`admin-card-${index}`} className="bg-white/5 backdrop-blur-sm border-white/10 hover:border-orange-500/30">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-lg ${roleColors[admin.role] || 'bg-gray-500/20'}`}>
                        <Shield className="w-6 h-6" />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-white mb-1">{admin.username}</CardTitle>
                        <div className="flex flex-wrap gap-3 text-sm text-gray-400 items-center">
                          <span>{admin.email}</span>
                          {admin.username !== localStorage.getItem('username') ? (
                            <Select 
                              value={admin.role} 
                              onValueChange={(newRole) => handleRoleChange(admin.id, newRole)}
                            >
                              <SelectTrigger 
                                className={`w-auto px-3 py-1 h-auto rounded-full text-xs font-medium border ${roleColors[admin.role] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}
                              >
                                <SelectValue>{roleLabels[admin.role] || admin.role}</SelectValue>
                              </SelectTrigger>
                              <SelectContent className="bg-gray-800 border-white/20">
                                <SelectItem value="managing_director" className="text-white hover:bg-white/10">
                                  <span className="text-red-400">● </span>Managing Director
                                </SelectItem>
                                <SelectItem value="administration_manager" className="text-white hover:bg-white/10">
                                  <span className="text-orange-400">● </span>Administration Manager
                                </SelectItem>
                                <SelectItem value="service_coordinator" className="text-white hover:bg-white/10">
                                  <span className="text-blue-400">● </span>Service Coordinator
                                </SelectItem>
                                <SelectItem value="account_manager" className="text-white hover:bg-white/10">
                                  <span className="text-green-400">● </span>Account Manager
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                              roleColors[admin.role] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                            } cursor-not-allowed opacity-60`}>
                              {roleLabels[admin.role] || admin.role}
                            </span>
                          )}
                          <span>Joined {formatDate(admin.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {admin.username !== localStorage.getItem('username') && (
                      <div className="flex gap-2">
                        <Button
                          data-testid={`edit-admin-${index}`}
                          onClick={() => openEditDialog(admin)}
                          size="sm"
                          variant="outline"
                          className="border-white/20 text-white hover:bg-white/10"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                        
                        <Button
                          data-testid={`delete-admin-${index}`}
                          onClick={() => deleteAdmin(admin.id)}
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-gray-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-2xl text-white">Edit Admin</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleEditAdmin} className="space-y-4">
            <div>
              <Label htmlFor="edit_email" className="text-gray-300">Email</Label>
              <Input
                id="edit_email"
                data-testid="edit-email-input"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                className="bg-white/10 border-white/20 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="edit_role" className="text-gray-300">Role</Label>
              <Select value={editFormData.role} onValueChange={(value) => setEditFormData({ ...editFormData, role: value })}>
                <SelectTrigger data-testid="edit-role-select" className="bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-white/20">
                  <SelectItem value="managing_director" className="text-white hover:bg-white/10">Managing Director</SelectItem>
                  <SelectItem value="administration_manager" className="text-white hover:bg-white/10">Administration Manager</SelectItem>
                  <SelectItem value="service_coordinator" className="text-white hover:bg-white/10">Service Coordinator</SelectItem>
                  <SelectItem value="account_manager" className="text-white hover:bg-white/10">Account Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="edit_password" className="text-gray-300">New Password (leave blank to keep current)</Label>
              <Input
                id="edit_password"
                data-testid="edit-password-input"
                type="password"
                value={editFormData.password}
                onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                className="bg-white/10 border-white/20 text-white"
              />
            </div>

            <div className="flex gap-3">
              <Button type="submit" data-testid="submit-edit-admin-btn" className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white border-0">
                Update Admin
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)} className="border-white/20 text-white hover:bg-white/10">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminUsersPage;
